import {
  type BaseStats,
  getStats,
  type StatsResponse,
} from '@lms/api-client/backend';
import { toLocalDateString } from '@lms/api-client/dates';
import useSWR from 'swr';
import { useLocale } from 'use-intl';

export interface StatsHookParams {
  locations: number[];
  period: 'since' | 'month' | 'year' | 'day';
  fromDate?: Date;
  courses?: number[];
  /** Only these curricula (all curricula when empty or unset). */
  curricula?: number[];
  /** Also fetch the same period last year (`previousSessions`). */
  compare?: boolean;
  /** Only these groups (all groups when empty or unset). */
  groups?: number[];
  /** Only students who signed up with these codes (all when empty or unset). */
  codes?: number[];
}

// Empty defaults so the dashboard can render its loading/empty states without
// null-checking every field. Mirrors the Next hook's `?? []` fallbacks.
const emptyBaseStats: BaseStats = {
  groups: 0,
  curriculums: 0,
  courses: 0,
  tenants: 0,
  suppliers: 0,
  tenantsWithoutPass: 0,
  suppliersWithoutPass: 0,
};

type HookResponse = Omit<StatsResponse, 'fromDate'> & {
  fromDate: Date;
  isLoading: boolean;
  error: undefined | Error | string;
};

// Ports features/dashboard/use-stats.tsx. In the Next app this fetched the
// `/api/admin/stats` route (which proxied `getStats`); the SPA calls the
// backend client directly via SWR.
export function useStats({
  locations,
  period,
  fromDate,
  courses,
  curricula,
  compare,
  groups,
  codes,
}: StatsHookParams): HookResponse {
  const locale = useLocale();

  const { data, error, isLoading } = useSWR(
    [
      'admin/stats',
      locations,
      period,
      fromDate?.toISOString(),
      courses,
      curricula,
      locale,
      compare,
      groups,
      codes,
    ],
    () =>
      getStats({
        location: locations,
        course: courses && courses.length > 0 ? courses : undefined,
        curriculum: curricula,
        period,
        fromDate: fromDate ? toLocalDateString(fromDate) : undefined,
        locale,
        compare,
        group: groups,
        code: codes,
      }),
    { keepPreviousData: true },
  );

  return {
    locations: data?.locations ?? [],
    educationTime: data?.educationTime ?? [],
    sessions: data?.sessions ?? [],
    previousSessions: data?.previousSessions ?? [],
    baseStats: data?.baseStats ?? emptyBaseStats,
    courses: data?.courses ?? [],
    curricula: data?.curricula ?? [],
    fromDate: new Date(data?.fromDate ?? fromDate ?? Date.now()),
    isLoading,
    error,
  };
}
